import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CalendarDays,
  ClipboardList,
  FolderKanban,
  RefreshCw,
  UsersRound,
} from "lucide-react";
import api from "../../services/api";
import DispatchBoard from "../../components/planner/DispatchBoard";
import DispatchFocusPanel from "../../components/planner/DispatchFocusPanel";
import PlanningCapacityPanel from "../../components/planner/PlanningCapacityPanel";
import PlanningHeader from "../../components/planner/PlanningHeader";
import ProjectTimelineDemo from "../../components/planner/ProjectTimelineDemo";
import TechnicianTimeline from "../../components/planner/TechnicianTimeline";
import WerkbonDrawer from "../../components/planner/WerkbonDrawer";
import PlannerTimeRequestPanel from "../../components/time-requests/PlannerTimeRequestPanel";
import {
  asArray,
  getCapacityTone,
  normalizeWerkbon,
  parseTimeToMinutes,
  toISODate,
  uniqueOptions,
} from "../../components/planner/planningUtils";
import {
  PLANNED_MINUTES_FALLBACK,
  parsePositiveWholeMinutes,
} from "../../utils/plannedMinutes";
import {
  getApiErrorMessage,
  hasApiErrorCode,
} from "../../utils/apiError";

const VIEW_TABS = [
  { key: "dispatch", label: "Werkvoorraad", icon: ClipboardList },
  { key: "planning", label: "Dagplanning", icon: CalendarDays },
  { key: "capacity", label: "Inzet & Capaciteit", icon: UsersRound },
  { key: "project_timeline", label: "Projecttijdlijn", icon: FolderKanban },
];

const TIMELINE_HOURS = [
  "07:00",
  "08:00",
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
];

const ACTIVE_VIEW_HINTS = {
  dispatch:
    "Werkvoorraad: bundel open werk, intake en nog niet geplande werkbonnen voordat je ze naar de dagplanning trekt.",
  planning:
    "Dagplanning: plan op monteur of ploeg en wissel tussen tijdlijn en lijst om conflicten en capaciteit rustiger te beoordelen.",
  capacity:
    "Inzet & Capaciteit: bekijk weekbelasting per monteur, zie waar nog ruimte zit en signaleer overbezetting eerder.",
  project_timeline:
    "Projecttijdlijn: bewaak samenhang tussen projectwerk, open taken en werkbonnen binnen dezelfde weekfocus.",
};

function filterWorkorders(items, filters) {
  const needle = String(filters?.query || "").trim().toLowerCase();

  return items.filter((item) => {
    if (filters?.status !== "all" && item.statusKey !== filters?.status) return false;
    if (filters?.project !== "all" && item.projectKey !== filters?.project) return false;
    if (filters?.client !== "all" && item.clientKey !== filters?.client) return false;
    if (filters?.discipline !== "all" && item.disciplineKey !== filters?.discipline) return false;
    if (filters?.priority !== "all" && item.priorityKey !== filters?.priority) return false;
    if (!needle) return true;

    return [
      item.werkbonNummer,
      item.title,
      item.clientName,
      item.locationLabel,
      item.projectLabel,
      item.assignedLabel,
    ]
      .join(" ")
      .toLowerCase()
      .includes(needle);
  });
}

function getWeekCapacityHours(monteur) {
  const configured = Number(monteur?.technicianProfile?.maxHoursPerWeek || 0);
  if (Number.isFinite(configured) && configured > 0) return configured;
  return 40;
}

function getDayCapacityHours(monteur) {
  const weeklyHours = getWeekCapacityHours(monteur);
  if (weeklyHours <= 16) return weeklyHours;
  return Number((weeklyHours / 5).toFixed(1));
}

function mergeWorkorderIntoCollection(items, targetId, patch) {
  const id = String(targetId || "").trim();
  if (!id) return items;
  return items.map((item) =>
    String(item?.id || item?._id || item?.werkbonId || "") === id
      ? { ...item, ...patch }
      : item
  );
}

export default function PlanningPage() {
  const navigate = useNavigate();

  const [dayOverviewItems, setDayOverviewItems] = useState([]);
  const [weekOverviewItems, setWeekOverviewItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [monteurs, setMonteurs] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeView, setActiveView] = useState("planning");
  const [selectedDate, setSelectedDate] = useState(() => toISODate(new Date()));
  const [activeWerkbonId, setActiveWerkbonId] = useState("");
  const [plannerMessage, setPlannerMessage] = useState(null);
  const [planningSaving, setPlanningSaving] = useState(false);
  const [targetSavingProjectId, setTargetSavingProjectId] = useState("");
  const [taskCreatingProjectId, setTaskCreatingProjectId] = useState("");
  const [taskReplanningId, setTaskReplanningId] = useState("");
  const [taskUpdatingId, setTaskUpdatingId] = useState("");
  const [taskDeletingId, setTaskDeletingId] = useState("");
  const [pendingTimeRequests, setPendingTimeRequests] = useState([]);
  const [timeRequestsError, setTimeRequestsError] = useState("");
  const [timeRequestActionId, setTimeRequestActionId] = useState("");
  const [dayPlanningViewMode, setDayPlanningViewMode] = useState("timeline");
  const [filters, setFilters] = useState({
    query: "",
    status: "all",
    project: "all",
    client: "all",
    discipline: "all",
    priority: "all",
  });
  const [planningDraft, setPlanningDraft] = useState({
    assignedTo: "",
    scopeId: "",
    plannedDate: "",
    plannedStartTime: "",
    plannedMinutes: PLANNED_MINUTES_FALLBACK,
  });

  const setMessage = useCallback(
    (
      text,
      tone = "bg-emerald-50 border border-emerald-200 text-emerald-800"
    ) => {
      setPlannerMessage({ text, tone });
    },
    []
  );

  const fetchPlannerData = useCallback(async () => {
    const [
      dayOverviewRes,
      weekOverviewRes,
      monteurRes,
      timeRequestRes,
      scopeRes,
      projectRes,
      taskRes,
    ] = await Promise.allSettled([
      api.get("/planning/overview", {
        params: {
          date: selectedDate,
          view: "day",
          includeUnscheduled: true,
        },
      }),
      api.get("/planning/overview", {
        params: {
          date: selectedDate,
          view: "week",
        },
      }),
      api.get("/users", { params: { role: "Monteur" } }),
      api.get("/time-extension-requests/pending"),
      api.get("/tenant/scopes"),
      api.get("/projects"),
      api.get("/tasks"),
    ]);

    return {
      dayOverviewItems:
        dayOverviewRes.status === "fulfilled" &&
        Array.isArray(dayOverviewRes.value?.data?.items)
          ? dayOverviewRes.value.data.items
          : [],
      weekOverviewItems:
        weekOverviewRes.status === "fulfilled" &&
        Array.isArray(weekOverviewRes.value?.data?.items)
          ? weekOverviewRes.value.data.items
          : [],
      monteurs:
        monteurRes.status === "fulfilled" ? asArray(monteurRes.value?.data) : [],
      pendingTimeRequests:
        timeRequestRes.status === "fulfilled" &&
        Array.isArray(timeRequestRes.value?.data)
          ? timeRequestRes.value.data
          : [],
      scopes:
        scopeRes.status === "fulfilled" &&
        Array.isArray(scopeRes.value?.data?.items)
          ? scopeRes.value.data.items
          : [],
      projects:
        projectRes.status === "fulfilled" && Array.isArray(projectRes.value?.data)
          ? projectRes.value.data
          : [],
      tasks:
        taskRes.status === "fulfilled" && Array.isArray(taskRes.value?.data)
          ? taskRes.value.data
          : [],
    };
  }, [selectedDate]);

  const applyPlannerData = useCallback((payload) => {
    setDayOverviewItems(Array.isArray(payload?.dayOverviewItems) ? payload.dayOverviewItems : []);
    setWeekOverviewItems(
      Array.isArray(payload?.weekOverviewItems) ? payload.weekOverviewItems : []
    );
    setMonteurs(Array.isArray(payload?.monteurs) ? payload.monteurs : []);
    setPendingTimeRequests(
      Array.isArray(payload?.pendingTimeRequests) ? payload.pendingTimeRequests : []
    );
    setScopes(Array.isArray(payload?.scopes) ? payload.scopes : []);
    setProjects(Array.isArray(payload?.projects) ? payload.projects : []);
    setTasks(Array.isArray(payload?.tasks) ? payload.tasks : []);
  }, []);

  useEffect(() => {
    let active = true;

    async function loadPlanner() {
      try {
        setLoading(true);
        setError("");
        setTimeRequestsError("");
        const payload = await fetchPlannerData();
        if (!active) return;
        applyPlannerData(payload);
      } catch (err) {
        if (!active) return;
        const message = getApiErrorMessage(err, "Planning laden mislukt");
        setError(message);
        setTimeRequestsError(message);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadPlanner();
    return () => {
      active = false;
    };
  }, [applyPlannerData, fetchPlannerData]);

  const normalizedDayWorkorders = useMemo(
    () => dayOverviewItems.map((item) => normalizeWerkbon(item, monteurs)),
    [dayOverviewItems, monteurs]
  );

  const normalizedWeekWorkorders = useMemo(
    () => weekOverviewItems.map((item) => normalizeWerkbon(item, monteurs)),
    [weekOverviewItems, monteurs]
  );

  const filterOptions = useMemo(
    () => ({
      statuses: uniqueOptions(normalizedDayWorkorders, "statusKey", "statusLabel"),
      projects: uniqueOptions(normalizedWeekWorkorders, "projectKey", "projectLabel"),
      clients: uniqueOptions(normalizedWeekWorkorders, "clientKey", "clientName"),
      disciplines: uniqueOptions(normalizedWeekWorkorders, "disciplineKey", "disciplineLabel"),
      priorities: uniqueOptions(normalizedWeekWorkorders, "priorityKey", "priorityLabel"),
    }),
    [normalizedDayWorkorders, normalizedWeekWorkorders]
  );

  const filteredDayWorkorders = useMemo(
    () => filterWorkorders(normalizedDayWorkorders, filters),
    [filters, normalizedDayWorkorders]
  );

  const filteredWeekWorkorders = useMemo(
    () => filterWorkorders(normalizedWeekWorkorders, filters),
    [filters, normalizedWeekWorkorders]
  );

  const dispatchItems = useMemo(
    () =>
      filteredDayWorkorders.filter(
        (item) =>
          !item.plannedDate ||
          item.statusKey === "gereed_voor_planning" ||
          item.statusKey === "concept"
      ),
    [filteredDayWorkorders]
  );

  const dayPlannedItems = useMemo(
    () => filteredDayWorkorders.filter((item) => item.plannedDate === selectedDate),
    [filteredDayWorkorders, selectedDate]
  );

  const timelineRows = useMemo(() => {
    return monteurs.map((monteur) => {
      const monteurId = String(monteur?._id || "");
      const rawItems = dayPlannedItems
        .filter((item) => String(item.primaryAssignedUserId || "") === monteurId)
        .map((item) => {
          const startMinutes = parseTimeToMinutes(item.startTime || "08:00");
          const durationMinutes = Math.max(Number(item.durationMinutes || 0), 30);
          const startHour = startMinutes / 60;
          const durationHours = Math.max(durationMinutes / 60, 0.5);
          return {
            ...item,
            startMinutes,
            endMinutes: startMinutes + durationMinutes,
            durationMinutes,
            timelineLeft: `${(startHour - 7) * 96}px`,
            timelineWidth: `${durationHours * 96}px`,
          };
        })
        .sort((a, b) => `${a.startTime}`.localeCompare(`${b.startTime}`));

      const items = rawItems.map((item, index) => {
        const hasConflict = rawItems.some((candidate, candidateIndex) => {
          if (candidateIndex === index) return false;
          return (
            item.startMinutes < candidate.endMinutes &&
            item.endMinutes > candidate.startMinutes
          );
        });

        return {
          ...item,
          hasConflict,
          timelineColor: hasConflict ? "bg-rose-600" : item.timelineColor,
        };
      });

      const dailyCapacityHours = getDayCapacityHours(monteur);
      const occupancyHours = items.reduce(
        (sum, item) => sum + item.durationMinutes / 60,
        0
      );
      const occupancyRatio =
        dailyCapacityHours > 0 ? occupancyHours / dailyCapacityHours : 0;

      return {
        id: monteurId || monteur.email || monteur.naam,
        monteurId,
        name: monteur?.naam || monteur?.email || "Monteur",
        assignmentCount: items.length,
        totalHours: occupancyHours,
        occupancyHours,
        occupancyRatio,
        dailyCapacityHours,
        capacityLabel: `${occupancyHours.toFixed(1)} / ${dailyCapacityHours.toFixed(
          1
        )} uur`,
        capacityTone: getCapacityTone(occupancyRatio),
        conflictCount: items.filter((item) => item.hasConflict).length,
        items,
      };
    });
  }, [dayPlannedItems, monteurs]);

  const weeklyCapacityRows = useMemo(() => {
    return monteurs
      .map((monteur) => {
        const monteurId = String(monteur?._id || "");
        const items = filteredWeekWorkorders
          .filter((item) => String(item.primaryAssignedUserId || "") === monteurId)
          .sort((a, b) => {
            const left = `${a.plannedDate || ""} ${a.startTime || ""}`;
            const right = `${b.plannedDate || ""} ${b.startTime || ""}`;
            return left.localeCompare(right, "nl-NL");
          });
        const plannedHours = items.reduce(
          (sum, item) => sum + Number(item.durationMinutes || 0) / 60,
          0
        );
        const maxHours = getWeekCapacityHours(monteur);
        const utilization = maxHours > 0 ? plannedHours / maxHours : 0;
        return {
          id: monteurId || monteur?.email || monteur?.naam,
          monteurId,
          name: monteur?.naam || monteur?.email || "Monteur",
          itemCount: items.length,
          items,
          plannedHours,
          maxHours,
          remainingHours: Number((maxHours - plannedHours).toFixed(1)),
          occupancyRatio: utilization,
          capacityTone: getCapacityTone(utilization),
        };
      })
      .filter((row) => row.items.length > 0 || filteredWeekWorkorders.length === 0);
  }, [filteredWeekWorkorders, monteurs]);

  const activeWerkbon = useMemo(
    () =>
      filteredDayWorkorders.find((item) => item.id === activeWerkbonId) ||
      normalizedDayWorkorders.find((item) => item.id === activeWerkbonId) ||
      filteredWeekWorkorders.find((item) => item.id === activeWerkbonId) ||
      normalizedWeekWorkorders.find((item) => item.id === activeWerkbonId) ||
      null,
    [
      activeWerkbonId,
      filteredDayWorkorders,
      filteredWeekWorkorders,
      normalizedDayWorkorders,
      normalizedWeekWorkorders,
    ]
  );

  useEffect(() => {
    if (!activeWerkbon) return;
    setPlanningDraft({
      assignedTo: activeWerkbon.primaryAssignedUserId || "",
      scopeId: activeWerkbon.scopeId || "",
      plannedDate: activeWerkbon.plannedDate || selectedDate,
      plannedStartTime: activeWerkbon.startTime || "08:00",
      plannedMinutes:
        activeWerkbon.durationMinutes || PLANNED_MINUTES_FALLBACK,
    });
  }, [activeWerkbon, selectedDate]);

  const summary = useMemo(() => {
    const capacityAlerts = weeklyCapacityRows.filter(
      (row) => row.capacityTone !== "ok"
    ).length;

    return {
      dispatch: dispatchItems.length,
      planning: dayPlannedItems.length,
      capacity: capacityAlerts,
      projecten: new Set(
        filteredWeekWorkorders
          .map((item) => item.projectKey)
          .filter((value) => value && value !== "zonder-project")
      ).size,
      monteurs: monteurs.length,
      timeRequests: pendingTimeRequests.length,
    };
  }, [
    dayPlannedItems.length,
    dispatchItems.length,
    filteredWeekWorkorders,
    monteurs.length,
    pendingTimeRequests.length,
    weeklyCapacityRows,
  ]);

  const refreshPlannerData = async (successMessage = "Planninggegevens ververst.") => {
    try {
      setError("");
      setTimeRequestsError("");
      const payload = await fetchPlannerData();
      applyPlannerData(payload);
      setMessage(successMessage);
    } catch (err) {
      const message = getApiErrorMessage(err, "Verversen mislukt");
      setError(message);
      setTimeRequestsError(message);
      setMessage(message, "bg-rose-50 border border-rose-200 text-rose-800");
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleOpenWerkbon = (item) => {
    setActiveWerkbonId(item.id);
    setPlannerMessage(null);
  };

  const handleProjectTargetsSave = async (projectId, payload) => {
    const id = String(projectId || "").trim();
    if (!id) return;

    try {
      setTargetSavingProjectId(id);
      const response = await api.put(`/projects/${id}/targets`, payload);
      const updatedProject = response?.data || null;

      if (updatedProject?._id) {
        setProjects((prev) =>
          prev.map((project) =>
            String(project?._id || "") === String(updatedProject._id || "")
              ? { ...project, ...updatedProject }
              : project
          )
        );
      }

      setMessage("Projecttargets opgeslagen.");
    } catch (err) {
      setMessage(
        getApiErrorMessage(err, "Projecttargets opslaan mislukt"),
        "bg-rose-50 border border-rose-200 text-rose-800"
      );
    } finally {
      setTargetSavingProjectId("");
    }
  };

  const handleCreateProjectTask = async (projectId, payload) => {
    const id = String(projectId || "").trim();
    if (!id) return;

    try {
      setTaskCreatingProjectId(id);
      const response = await api.post("/tasks", {
        projectId: id,
        title: payload?.title,
        plannedDate: payload?.plannedDate || null,
        prio: payload?.prio || "normaal",
        status: "open",
        kind: "taak",
      });

      if (response?.data?._id) {
        setTasks((prev) => [response.data, ...prev]);
      }
      setMessage("Taak op tijdlijn toegevoegd.");
    } catch (err) {
      setMessage(
        getApiErrorMessage(err, "Taak aanmaken mislukt"),
        "bg-rose-50 border border-rose-200 text-rose-800"
      );
    } finally {
      setTaskCreatingProjectId("");
    }
  };

  const handleReplanProjectTask = async (taskId, plannedDate) => {
    const id = String(taskId || "").trim();
    if (!id) return;

    const previous = tasks;
    setTaskReplanningId(id);
    setTasks((prev) =>
      prev.map((task) =>
        String(task?._id || task?.id || "") === id
          ? { ...task, plannedDate: plannedDate || null }
          : task
      )
    );

    try {
      const response = await api.patch(`/tasks/${id}/planning`, {
        plannedDate: plannedDate || null,
      });

      const updated = response?.data || null;
      if (updated?._id) {
        setTasks((prev) =>
          prev.map((task) =>
            String(task?._id || task?.id || "") === String(updated._id || "")
              ? { ...task, ...updated }
              : task
          )
        );
      }
      setMessage("Taakplanning bijgewerkt.");
    } catch (err) {
      setTasks(previous);
      setMessage(
        getApiErrorMessage(err, "Taak herplannen mislukt"),
        "bg-rose-50 border border-rose-200 text-rose-800"
      );
    } finally {
      setTaskReplanningId("");
    }
  };

  const handleUpdateProjectTask = async (taskId, payload) => {
    const id = String(taskId || "").trim();
    if (!id) return false;

    try {
      setTaskUpdatingId(id);
      const response = await api.patch(`/tasks/${id}`, {
        title: String(payload?.title || "").trim(),
        plannedDate: payload?.plannedDate || null,
        prio: payload?.prio || "normaal",
        status: payload?.status || "open",
      });
      const updated = response?.data || null;
      if (updated?._id) {
        setTasks((prev) =>
          prev.map((task) =>
            String(task?._id || task?.id || "") === String(updated._id || "")
              ? { ...task, ...updated }
              : task
          )
        );
      }
      setMessage("Taak bijgewerkt.");
      return true;
    } catch (err) {
      setMessage(
        getApiErrorMessage(err, "Taak bijwerken mislukt"),
        "bg-rose-50 border border-rose-200 text-rose-800"
      );
      return false;
    } finally {
      setTaskUpdatingId("");
    }
  };

  const handleDeleteProjectTask = async (taskId) => {
    const id = String(taskId || "").trim();
    if (!id) return false;

    try {
      setTaskDeletingId(id);
      await api.delete(`/tasks/${id}`);
      setTasks((prev) =>
        prev.filter((task) => String(task?._id || task?.id || "") !== id)
      );
      setMessage("Taak verwijderd.");
      return true;
    } catch (err) {
      setMessage(
        getApiErrorMessage(err, "Taak verwijderen mislukt"),
        "bg-rose-50 border border-rose-200 text-rose-800"
      );
      return false;
    } finally {
      setTaskDeletingId("");
    }
  };

  const handleTimeRequestDecision = async (item, status) => {
    if (!item?._id) return;

    try {
      setTimeRequestActionId(item._id);
      setTimeRequestsError("");
      await api.patch(`/time-extension-requests/${item._id}`, { status });
      await refreshPlannerData(
        status === "approved"
          ? "Extra tijd goedgekeurd en planning verlengd."
          : "Extra tijd verzoek afgewezen."
      );
    } catch (err) {
      const message = getApiErrorMessage(
        err,
        "Extra tijd verzoek verwerken mislukt"
      );
      setTimeRequestsError(message);
      setMessage(message, "bg-rose-50 border border-rose-200 text-rose-800");
    } finally {
      setTimeRequestActionId("");
    }
  };

  const handleDragStart = (event, item) => {
    event.dataTransfer.setData(
      "application/logboek24-werkbon",
      JSON.stringify({
        id: item.id,
        durationMinutes: item.durationMinutes,
        startTime: item.startTime,
        scopeId: item.scopeId || "",
      })
    );
    event.dataTransfer.effectAllowed = "move";
  };

  const handleTimelineDrop = async (payload, row, slot = {}) => {
    if (!payload?.id || !row?.monteurId) return;
    const nextStartTime = slot.plannedStartTime || payload.startTime || "08:00";
    const nextDurationMinutes = Number(
      payload.durationMinutes || PLANNED_MINUTES_FALLBACK
    );

    try {
      await api.put(`/werkbonnen/${payload.id}/planning`, {
        assignedTo: row.monteurId,
        assignedUsers: [row.monteurId],
        plannedDate: selectedDate,
        plannedStartTime: nextStartTime,
        scopeId: payload.scopeId || "",
        plannedMinutes: nextDurationMinutes,
        plannedDurationMinutes: nextDurationMinutes,
      });

      setDayOverviewItems((prev) =>
        mergeWorkorderIntoCollection(prev, payload.id, {
          assignedTo: { userId: row.monteurId, naam: row.name, primary: true },
          assignedUsers: [{ userId: row.monteurId, naam: row.name, primary: true }],
          plannedDate: selectedDate,
          plannedStartTime: nextStartTime,
          plannedMinutes: nextDurationMinutes,
          plannedDurationMinutes: nextDurationMinutes,
          status: "gepland",
        })
      );

      await refreshPlannerData(`Werkbon ingepland op ${row.name} om ${nextStartTime}.`);
    } catch (err) {
      const message = hasApiErrorCode(err, "VALIDATION_ERROR")
        ? "Controleer monteur, datum en tijd; de planning is ongeldig."
        : getApiErrorMessage(err, "Werkbon plannen mislukt");
      setMessage(message, "bg-rose-50 border border-rose-200 text-rose-800");
    }
  };

  const handlePlanningDraftChange = (key, value) => {
    setPlanningDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handlePlanningSave = async () => {
    if (!activeWerkbon) return;

    const validatedPlannedMinutes = parsePositiveWholeMinutes(
      planningDraft.plannedMinutes
    );
    if (!validatedPlannedMinutes) {
      setMessage(
        "Geplande tijd moet een positief geheel aantal minuten zijn.",
        "bg-rose-50 border border-rose-200 text-rose-800"
      );
      return;
    }

    try {
      setPlanningSaving(true);
      await api.put(`/werkbonnen/${activeWerkbon.id}/planning`, {
        assignedTo: planningDraft.assignedTo || null,
        assignedUsers: planningDraft.assignedTo ? [planningDraft.assignedTo] : [],
        scopeId: planningDraft.scopeId || null,
        plannedDate: planningDraft.plannedDate || null,
        plannedStartTime: planningDraft.plannedStartTime || "",
        plannedMinutes: validatedPlannedMinutes,
        plannedDurationMinutes: validatedPlannedMinutes,
      });

      await refreshPlannerData("Planning opgeslagen.");
    } catch (err) {
      const message = hasApiErrorCode(err, "VALIDATION_ERROR")
        ? "Controleer de ingevoerde planning en probeer opnieuw."
        : getApiErrorMessage(err, "Planning opslaan mislukt");
      setMessage(message, "bg-rose-50 border border-rose-200 text-rose-800");
    } finally {
      setPlanningSaving(false);
    }
  };

  const handleSendBackToDispatch = async () => {
    if (!activeWerkbon) return;

    try {
      setPlanningSaving(true);

      await api.put(`/werkbonnen/${activeWerkbon.id}/planning`, {
        assignedTo: null,
        assignedUsers: [],
        scopeId: null,
        plannedDate: null,
        plannedStartTime: "",
        plannedMinutes: PLANNED_MINUTES_FALLBACK,
        plannedDurationMinutes: PLANNED_MINUTES_FALLBACK,
      });

      await api.post(`/werkbonnen/${activeWerkbon.id}/status`, {
        toStatus: "gereed_voor_planning",
        override: true,
      });

      setPlanningDraft({
        assignedTo: "",
        scopeId: "",
        plannedDate: "",
        plannedStartTime: "",
        plannedMinutes: PLANNED_MINUTES_FALLBACK,
      });
      await refreshPlannerData("Werkbon teruggezet naar de werkvoorraad.");
    } catch (err) {
      setMessage(
        getApiErrorMessage(err, "Terugzetten naar werkvoorraad mislukt"),
        "bg-rose-50 border border-rose-200 text-rose-800"
      );
    } finally {
      setPlanningSaving(false);
    }
  };

  return (
    <section className="space-y-4">
      <PlanningHeader
        summary={summary}
        activeView={activeView}
        activeViewHint={ACTIVE_VIEW_HINTS[activeView]}
        viewTabs={{
          tabs: VIEW_TABS,
          summaryTiles: [
            { label: "Werkvoorraad", valueKey: "dispatch", icon: ClipboardList },
            { label: "Dagplanning", valueKey: "planning", icon: CalendarDays },
            { label: "Capaciteit", valueKey: "capacity", icon: AlertTriangle },
            { label: "Projecten", valueKey: "projecten", icon: FolderKanban },
            { label: "Monteurs", valueKey: "monteurs", icon: UsersRound },
          ],
          refreshIcon: RefreshCw,
        }}
        plannerMessage={plannerMessage}
        onViewChange={setActiveView}
        onRefresh={() => refreshPlannerData()}
      />

      {loading ? (
        <div className="rounded-[26px] border border-slate-200 bg-white px-6 py-10 text-sm text-slate-500 shadow-sm">
          Planning laden...
        </div>
      ) : error ? (
        <div className="rounded-[26px] border border-rose-200 bg-rose-50 px-6 py-10 text-sm text-rose-700 shadow-sm">
          {error}
        </div>
      ) : (
        <>
          <PlannerTimeRequestPanel
            items={pendingTimeRequests}
            loading={false}
            error={timeRequestsError}
            actingId={timeRequestActionId}
            onRefresh={() => refreshPlannerData("Extra tijd verzoeken ververst.")}
            onApprove={(item) => handleTimeRequestDecision(item, "approved")}
            onReject={(item) => handleTimeRequestDecision(item, "rejected")}
          />

          {activeView === "planning" ? (
            <div className="space-y-4">
              <TechnicianTimeline
                rows={timelineRows}
                hours={TIMELINE_HOURS}
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                onOpen={handleOpenWerkbon}
                onDropCard={handleTimelineDrop}
                onDragStart={handleDragStart}
                viewMode={dayPlanningViewMode}
                onViewModeChange={setDayPlanningViewMode}
              />

              <div className="grid gap-4 xl:grid-cols-[420px_minmax(0,1fr)]">
                <DispatchBoard
                  items={dispatchItems}
                  filters={filters}
                  filterOptions={filterOptions}
                  onFilterChange={handleFilterChange}
                  onOpen={handleOpenWerkbon}
                  onDragStart={handleDragStart}
                />

                <WerkbonDrawer
                  item={activeWerkbon}
                  planningDraft={planningDraft}
                  monteurs={monteurs}
                  scopes={scopes}
                  planningSaving={planningSaving}
                  plannerMessage={plannerMessage}
                  onClose={() => setActiveWerkbonId("")}
                  onPlanningChange={handlePlanningDraftChange}
                  onPlanningSave={handlePlanningSave}
                  onSendBackToDispatch={handleSendBackToDispatch}
                />
              </div>
            </div>
          ) : activeView === "capacity" ? (
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
              <PlanningCapacityPanel
                rows={weeklyCapacityRows}
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                onOpen={handleOpenWerkbon}
              />

              <WerkbonDrawer
                item={activeWerkbon}
                planningDraft={planningDraft}
                monteurs={monteurs}
                scopes={scopes}
                planningSaving={planningSaving}
                plannerMessage={plannerMessage}
                onClose={() => setActiveWerkbonId("")}
                onPlanningChange={handlePlanningDraftChange}
                onPlanningSave={handlePlanningSave}
                onSendBackToDispatch={handleSendBackToDispatch}
              />
            </div>
          ) : activeView === "project_timeline" ? (
            <ProjectTimelineDemo
              projects={projects}
              items={filteredWeekWorkorders}
              tasks={tasks}
              savingProjectId={targetSavingProjectId}
              creatingTaskProjectId={taskCreatingProjectId}
              replanningTaskId={taskReplanningId}
              updatingTaskId={taskUpdatingId}
              deletingTaskId={taskDeletingId}
              onSaveTargets={handleProjectTargetsSave}
              onCreateTask={handleCreateProjectTask}
              onReplanTask={handleReplanProjectTask}
              onUpdateTask={handleUpdateProjectTask}
              onDeleteTask={handleDeleteProjectTask}
              onProjectOpen={(project) => {
                const projectId = String(
                  project?.projectId || project?.projectKey || ""
                ).trim();
                navigate(
                  projectId
                    ? `/pl/projecten?project=${encodeURIComponent(projectId)}`
                    : "/pl/projecten"
                );
              }}
              onWorkorderOpen={handleOpenWerkbon}
            />
          ) : (
            <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)_360px]">
              <DispatchBoard
                items={dispatchItems}
                filters={filters}
                filterOptions={filterOptions}
                onFilterChange={handleFilterChange}
                onOpen={handleOpenWerkbon}
                onDragStart={handleDragStart}
              />

              <div className="min-w-0">
                <DispatchFocusPanel
                  items={dispatchItems}
                  onOpen={handleOpenWerkbon}
                  onDragStart={handleDragStart}
                />
              </div>

              <WerkbonDrawer
                item={activeWerkbon}
                planningDraft={planningDraft}
                monteurs={monteurs}
                scopes={scopes}
                planningSaving={planningSaving}
                plannerMessage={plannerMessage}
                onClose={() => setActiveWerkbonId("")}
                onPlanningChange={handlePlanningDraftChange}
                onPlanningSave={handlePlanningSave}
                onSendBackToDispatch={handleSendBackToDispatch}
              />
            </div>
          )}
        </>
      )}
    </section>
  );
}
